import React from "react";
import { useTranslation } from "react-i18next";

// Components
import Modal from "../../../../components/common/Modal";
import PrimaryButton, {
  PrimaryButtonType,
} from "../../../../components/common/PrimaryButton";

// Styles
import styles from "./styles.module.scss";

interface Props {
  isDisabled?: boolean;
  open: boolean;
  onClose: () => void;
}

/**
 * WithdrawalSuccessModal Component
 *
 * Displays a success modal when a withdrawal request is completed successfully.
 * Shows a success icon, title, subtitle, and close button.
 *
 * Features:
 * - Success icon with checkmark
 * - Success title in Arabic
 * - Informative subtitle about transfer timing
 * - Close button
 */
const WithdrawalSuccessModal: React.FC<Props> = ({
  isDisabled,
  open,
  onClose,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      size="normal"
      customModalContainerClassName={styles.withdraw_success_modal}>
      <div className={styles.withdraw_success_container}>
        {/* Success Icon */}
        <div className={styles.success_icon_bg} />
        <div className={styles.success_icon_container}>
          <div className={styles.success_icon}>
            <img
              src="/assets/images/svg/wallet_withdraw_success.svg"
              alt="success"
            />
          </div>
        </div>

        {/* Success Title */}
        <div className={styles.title_container}>
          {isDisabled ? (
            <h1>{t("balance_bulk_disable_withdraw_success_title")}</h1>
          ) : (
            <h1>{t("balance_bulk_withdrawal_success_title")}</h1>
          )}
        </div>

        {/* Success Subtitle */}
        <div className={styles.subtitle_container}>
          <p className={styles.success_subtitle}>
            {(() => {
              const subtitle = t(
                "balance_bulk_request_withdraw_sueccess_subtitle"
              );
              const H1 = "48 ساعة عمل";
              const H2 = "لكننا نسعى لتحويله في أقرب وقت ممكن.";
              const nodes: React.ReactNode[] = [];
              let remaining = subtitle;
              while (remaining.length) {
                const i1 = remaining.indexOf(H1);
                const i2 = remaining.indexOf(H2);
                const has1 = i1 !== -1;
                const has2 = i2 !== -1;
                if (!has1 && !has2) {
                  nodes.push(remaining);
                  break;
                }
                const nextIdx = !has2 || (has1 && i1 < i2) ? i1 : i2;
                const match = nextIdx === i1 ? H1 : H2;
                if (nextIdx > 0) nodes.push(remaining.slice(0, nextIdx));
                if (match === H1) {
                  nodes.push(
                    <span
                      key={nodes.length}
                      className={styles.highlighted_text}>
                      {match}
                    </span>
                  );
                } else {
                  nodes.push(
                    <span key={nodes.length} className={styles.black_text}>
                      {match}
                    </span>
                  );
                }
                remaining = remaining.slice(nextIdx + match.length);
              }
              return <>{nodes}</>;
            })()}
          </p>
        </div>

        {/* Action Button */}
        <div className={styles["button_cont"]}>
          <PrimaryButton
            id="contract_details_add_adress_confirm"
            buttonType={PrimaryButtonType.PRIMARY_LIGHT}
            onClick={onClose}>
            <p className={styles.button_text}>{t("lbl_close")}</p>
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default WithdrawalSuccessModal;
